import { Box, Button, InputAdornment, InputAdornmentProps } from "@mui/material";
import {
  DateTimePicker,
  PickersTextField,
  PickersTextFieldProps,
  renderTimeViewClock,
} from "@mui/x-date-pickers";
import { PickersActionBarProps } from "@mui/x-date-pickers/PickersActionBar";
import { usePickerActionsContext, usePickerContext } from "@mui/x-date-pickers/hooks";
import React, { forwardRef } from "react";

import { DATETIME_FORMATS } from "@/common/constants/common";

import AppIconButton from "../Button/AppIconButton";
import AppIcon from "../Icons/AppIcon";

const CustomPickersTextFieldWithRef = forwardRef(function CustomPickersTextField(
  props: PickersTextFieldProps,
  ref: React.Ref<HTMLDivElement>,
) {
  return <PickersTextField {...props} ref={ref} />;
});

type CustomInputAdornmentProps = InputAdornmentProps & {
  onClear?: () => void;
};

function CustomInputAdornment(props: CustomInputAdornmentProps) {
  const { children, ...other } = props;

  const pickerContext = usePickerContext();

  return (
    <InputAdornment {...other}>
      {pickerContext.value && (
        <AppIconButton
          size='small'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            pickerContext.setValue(null);
          }}
        >
          <AppIcon of='clear' />
        </AppIconButton>
      )}

      {/* Default icons (e.g. open picker) */}
      {children}
    </InputAdornment>
  );
}

function ActionBar(props: PickersActionBarProps) {
  const { setValueToToday, cancelValueChanges, acceptValueChanges } = usePickerActionsContext();

  return (
    <Box
      sx={{
        gridColumn: "2 / 4",
        gridRow: 3,
        display: "flex",
        justifyContent: "flex-end",
        p: 1,
        gap: 0.5,
      }}
    >
      <Button onClick={setValueToToday}>Today</Button>
      <Button onClick={cancelValueChanges}>Cancel</Button>
      <Button onClick={acceptValueChanges}>OK</Button>
    </Box>
  );
}

interface Props extends React.ComponentProps<typeof DateTimePicker> {}

export function AppDateTimePicker(props: Props) {
  const { format, slots, slotProps, ...rest } = props;

  return (
    <DateTimePicker
      ampm={false}
      format={format || DATETIME_FORMATS.DISPLAY_DATETIME}
      // handle v7 -> v8 breaking change
      // https://mui.com/x/migration/migration-pickers-v7/
      enableAccessibleFieldDOMStructure={slots?.textField ? false : true}
      viewRenderers={{
        hours: renderTimeViewClock,
        minutes: renderTimeViewClock,
        seconds: undefined,
      }}
      slots={{
        actionBar: ActionBar,
        textField: CustomPickersTextFieldWithRef,
        inputAdornment: CustomInputAdornment,
        ...slots,
      }}
      slotProps={{
        inputAdornment: { position: "end" },
        ...slotProps,
      }}
      {...rest}
    />
  );
}
